import { useEffect } from "react";

/**
 * Lightweight per-page SEO hook — sets <title>, meta description,
 * og:title, og:description, og:url, twitter:title, twitter:description.
 * Restores previous values on unmount so SPA navigation stays clean.
 */
const setMeta = (selector, attr, value) => {
  if (!value) return null;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [, k, v] = /\[([^=]+)="([^"]+)"\]/.exec(selector) || [];
    if (k && v) el.setAttribute(k, v);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute(attr);
  el.setAttribute(attr, value);
  return prev;
};

const useSEO = ({ title, description, path } = {}) => {
  useEffect(() => {
    const prevTitle = document.title;
    const fullTitle = title ? `${title} · Shaurya Travels` : "Shaurya Travels — Handcrafted journeys across India & beyond";
    document.title = fullTitle;

    const prevDesc = setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:title"]', "content", fullTitle);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[name="twitter:title"]', "content", fullTitle);
    setMeta('meta[name="twitter:description"]', "content", description);

    const canonicalEl = document.head.querySelector('link[rel="canonical"]');
    let prevCanonical = null;
    if (canonicalEl && path) {
      prevCanonical = canonicalEl.getAttribute("href");
      const base = window.location.origin || "https://shauryatravels.in";
      canonicalEl.setAttribute("href", `${base}${path}`);
    }

    const ogUrlEl = document.head.querySelector('meta[property="og:url"]');
    let prevOgUrl = null;
    if (ogUrlEl && path) {
      prevOgUrl = ogUrlEl.getAttribute("content");
      const base = window.location.origin || "https://shauryatravels.in";
      ogUrlEl.setAttribute("content", `${base}${path}`);
    }

    return () => {
      document.title = prevTitle;
      if (prevDesc !== null) setMeta('meta[name="description"]', "content", prevDesc);
      if (canonicalEl && prevCanonical !== null) canonicalEl.setAttribute("href", prevCanonical);
      if (ogUrlEl && prevOgUrl !== null) ogUrlEl.setAttribute("content", prevOgUrl);
    };
  }, [title, description, path]);
};

export default useSEO;
